import React, { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, onValue, update } from 'firebase/database';

const UserLicenseManagementPanel = () => {
    const [promoCodes, setPromoCodes] = useState({});
    const [allLicenses, setAllLicenses] = useState({});
    const [loading, setLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortOrder, setSortOrder] = useState('desc');

    const decodeEmailKey = (key) => {
        if (!key) return 'N/A';
        return key.replace(/_at_/g, '@').replace(/_dot_/g, '.');
    };

    useEffect(() => {
        const promoRef = ref(database, 'licencesPremium');
        const licensesRef = ref(database, 'licenses');

        const unsubPromo = onValue(promoRef, (snapshot) => {
            if (snapshot.exists()) setPromoCodes(snapshot.val());
        });

        const unsubLicenses = onValue(licensesRef, (snapshot) => {
            if (snapshot.exists()) setAllLicenses(snapshot.val());
        });

        setLoading(false);
        return () => {
            unsubPromo();
            unsubLicenses();
        };
    }, []);

    const handleDeleteUser = async (key, email, isOnlyTrial) => {
        if (!window.confirm(`¿Estás seguro de que deseas eliminar permanentemente a ${email}? Esta acción no se puede deshacer.`)) return;
        try {
            const updates = {};
            updates[`licenses/${key}`] = null;
            if (!isOnlyTrial) {
                updates[`licencesPremium/${key}`] = null;
            }
            await update(ref(database), updates);
            alert('Registro eliminado correctamente');
        } catch (e) {
            alert('Error al eliminar: ' + e.message);
        }
    };

    const handleCleanupInactives = async () => {
        const inactiveTrials = Object.entries(allLicenses).filter(([key, data]) => !data.isActive && !promoCodes[key]);
        const inactivePremium = Object.entries(promoCodes).filter(([key, data]) => !data.isActive);

        const totalToClean = inactiveTrials.length + inactivePremium.length;

        if (totalToClean === 0) {
            alert('No hay usuarios inactivos para limpiar.');
            return;
        }

        if (!window.confirm(
            `⚠️ ¡ADVERTENCIA CRÍTICA! ⚠️\n\n` +
            `Vas a eliminar ${totalToClean} usuarios de la base de datos.\n\n` +
            `🔹 IMPORTANTE: Al borrar estos registros, los usuarios PERDERÁN su historial en este nodo.\n` +
            `🔹 RECUERDA: Si eliminas a un usuario que terminó su periodo de PRUEBA, este PODRÁ ACCEDER NUEVAMENTE al periodo de prueba gratuito como si fuera un usuario nuevo.\n\n` +
            `¿Deseas proceder con la eliminación masiva?`
        )) return;

        setIsUpdating(true);
        try {
            const updates = {};
            inactiveTrials.forEach(([key]) => { updates[`licenses/${key}`] = null; });
            inactivePremium.forEach(([key]) => {
                updates[`licencesPremium/${key}`] = null;
                updates[`licenses/${key}`] = null;
            });
            await update(ref(database), updates);
            alert(`${totalToClean} registros eliminados.`);
        } catch (e) {
            alert('Error en la limpieza: ' + e.message);
        } finally {
            setIsUpdating(false);
        }
    };

    const getProcessedUsers = () => {
        const users = [];
        const now = Date.now();

        Object.entries(allLicenses).forEach(([key, data]) => {
            if (!promoCodes[key]) {
                const expirationTime = data.expirationDate || 0;
                users.push({
                    key,
                    ...data,
                    email: data.email || decodeEmailKey(key),
                    isActive: data.isActive !== false && (expirationTime === 0 || expirationTime > now),
                    isOnlyTrial: true,
                    isManual: false,
                    originLabel: '🧪 PRUEBA'
                });
            }
        });

        Object.entries(promoCodes).forEach(([key, data]) => {
            const activatedBy = String(data.activatedBy || '').toLowerCase().trim();
            const isManual = activatedBy === 'fallback_auto' || activatedBy === 'manual_test';

            let label = isManual ? '⚙️ MANUAL (ADMIN)' : '🤖 SISTEMA (AUTO)';

            const userEmail = data.email || decodeEmailKey(key);
            const expirationTime = data.premiumEndDate || data.expirationDate || 0;
            const isActive = data.isActive !== false && (expirationTime === 0 || expirationTime > now);

            users.push({
                key,
                ...data,
                email: userEmail,
                isActive: isActive,
                isOnlyTrial: false,
                isManual: isManual,
                originLabel: label
            });
        });

        let processed = users.filter(user => {
            let matchesFilter = true;
            if (filter === 'trial') matchesFilter = user.isOnlyTrial;
            else if (filter === 'manual') matchesFilter = user.isManual;
            else if (filter === 'system') matchesFilter = !user.isManual && !user.isOnlyTrial;

            const matchesSearch = !searchTerm || (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesStatus = statusFilter === 'all' ? true :
                (statusFilter === 'active' ? user.isActive : !user.isActive);

            return matchesFilter && matchesSearch && matchesStatus;
        });

        processed.sort((a, b) => {
            const timeA = a.premiumEndDate || a.expirationDate || 0;
            const timeB = b.premiumEndDate || b.expirationDate || 0;
            return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
        });

        return processed;
    };

    const filteredUsers = getProcessedUsers();

    if (loading) return <div className="loading">Cargando gestión de usuarios...</div>;

    return (
        <div className="admin-panel-card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 'calc(100vh - 180px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '15px', flexShrink: 0 }}>
                <h2 style={{ margin: 0 }}>Gestión de Usuarios y Licencias</h2>

                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="Buscar usuario (email)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            list="user-emails"
                            style={{
                                padding: '8px 12px',
                                borderRadius: '20px',
                                border: '1px solid #444',
                                background: 'var(--bg-primary)',
                                color: 'white',
                                width: '250px',
                                outline: 'none'
                            }}
                        />
                        <datalist id="user-emails">
                            {Object.values(allLicenses).map(l => <option key={l.email} value={l.email} />)}
                        </datalist>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button onClick={() => setFilter('all')} style={{ padding: '6px 12px', borderRadius: '4px', background: filter === 'all' ? 'var(--accent)' : '#444', color: 'white', border: 'none', cursor: 'pointer' }}>Todos</button>
                        <button onClick={() => setFilter('trial')} style={{ padding: '6px 12px', borderRadius: '4px', background: filter === 'trial' ? '#9b59b6' : '#444', color: 'white', border: 'none', cursor: 'pointer' }}>Pruebas</button>
                        <button onClick={() => setFilter('manual')} style={{ padding: '6px 12px', borderRadius: '4px', background: filter === 'manual' ? '#e67e22' : '#444', color: 'white', border: 'none', cursor: 'pointer' }}>Manual</button>
                        <button onClick={() => setFilter('system')} style={{ padding: '6px 12px', borderRadius: '4px', background: filter === 'system' ? '#3498db' : '#444', color: 'white', border: 'none', cursor: 'pointer' }}>Sistema (Auto)</button>
                        <button
                            onClick={handleCleanupInactives}
                            disabled={isUpdating}
                            style={{ padding: '6px 12px', borderRadius: '4px', background: '#e74c3c', color: 'white', border: 'none', cursor: 'pointer', marginLeft: '10px', fontSize: '0.8rem', fontWeight: 'bold' }}
                        >
                            🧹 Limpiar Inactivos
                        </button>
                    </div>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #444', color: '#888' }}>
                            <th style={{ textAlign: 'left', padding: '10px' }}>Email</th>
                            <th style={{ textAlign: 'left', padding: '10px' }}>Tipo / Origen</th>
                            <th
                                style={{ textAlign: 'left', padding: '10px', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                            >
                                Expiración {sortOrder === 'asc' ? '↑' : '↓'}
                            </th>
                            <th
                                style={{ textAlign: 'left', padding: '10px', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setStatusFilter(prev => prev === 'all' ? 'active' : (prev === 'active' ? 'inactive' : 'all'))}
                            >
                                Estado {statusFilter === 'all' ? '🔍' : (statusFilter === 'active' ? '✅' : '❌')}
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px' }}>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr><td colSpan="5" style={{ padding: '20px', textAlign: 'center', opacity: 0.5 }}>No se encontraron usuarios con este filtro</td></tr>
                        ) : (
                            filteredUsers.map((user) => {
                                const expiration = user.expirationDate || user.premiumEndDate;

                                return (
                                    <tr key={user.key} style={{ borderBottom: '1px solid #333' }}>
                                        <td style={{ padding: '10px' }}>
                                            {user.email}<br />
                                            <small style={{ opacity: 0.5 }}>ID: {user.userId || 'N/A'}</small>
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            <span style={{
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                background: user.isOnlyTrial ? 'rgba(155, 89, 182, 0.2)' : (user.isManual ? 'rgba(230, 126, 34, 0.2)' : 'rgba(52, 152, 219, 0.2)'),
                                                color: user.isOnlyTrial ? '#9b59b6' : (user.isManual ? '#e67e22' : '#3498db')
                                            }}>
                                                {user.originLabel}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            {expiration ? new Date(expiration).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            <span style={{ color: user.isActive ? '#2ecc71' : '#e74c3c' }}>
                                                {user.isActive ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            <button
                                                onClick={() => handleDeleteUser(user.key, user.email, user.isOnlyTrial)}
                                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
                                                title="Eliminar registro"
                                            >
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default UserLicenseManagementPanel;
